import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table';
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Image as ImageIcon, 
  Music,
  AlertCircle,
  Check,
  X,
  Settings
} from 'lucide-react';
import { toast } from 'sonner';
import CategorySequenceAssignment from './CategorySequenceAssignment';
import { CategoryImageUpload } from './CategoryImageUpload';
import { UploadedImage } from '@/hooks/useCategoryImageUpload';

interface Category {
  id: string;
  name: string;
  slug: string;
  description: string;
  software_type: string;
  holiday_type: string | null;
  featured: boolean;
  sort_order: number;
  image_url: string | null;
  image_alt: string | null;
  created_at: string;
  updated_at: string;
}

interface CategoryFormData {
  name: string;
  description: string;
  software_type: string;
  holiday_type: string;
  featured: boolean;
  image_url: string;
  image_alt: string;
  uploadedImage?: UploadedImage | null;
}

export default function CategoriesManagement() {
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [activeView, setActiveView] = useState<'categories' | 'assignments'>('categories');
  const [formData, setFormData] = useState<CategoryFormData>({
    name: '',
    description: '',
    software_type: 'xLights',
    holiday_type: '',
    featured: false,
    image_url: '',
    image_alt: '',
    uploadedImage: null
  });

  const queryClient = useQueryClient();

  // Fetch categories
  const { data: categories = [], isLoading, error } = useQuery({
    queryKey: ['admin-categories'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('categories')
        .select(`
          *,
          category_songs (count)
        `)
        .order('sort_order', { ascending: true });

      if (error) throw error;
      return data.map(category => ({
        ...category,
        sequence_count: category.category_songs?.[0]?.count || 0
      })) as (Category & { sequence_count: number })[];
    },
  });

  // Create category mutation
  const createCategoryMutation = useMutation({
    mutationFn: async (data: CategoryFormData) => {
      // Check authentication first
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      console.log('🔐 Category create auth check:', {
        hasSession: !!session,
        userId: session?.user?.id,
        email: session?.user?.email,
        hasAccessToken: !!session?.access_token
      });

      if (sessionError || !session) {
        console.error('❌ Category create - no valid session');
        throw new Error('You must be logged in to create categories');
      }

      const slug = data.name.toLowerCase()
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-');

      const categoryData = {
        name: data.name,
        slug,
        description: data.description,
        software_type: data.software_type,
        holiday_type: data.holiday_type || null,
        featured: data.featured,
        image_url: data.image_url || null,
        image_alt: data.image_alt || null,
        sort_order: categories.length + 1
      };

      // Try normal insert first
      let insertResult = await supabase
        .from('categories')
        .insert(categoryData);

      // If insert fails with 400/403 error, try service role fallback
      if (insertResult.error?.message?.includes('403') || 
          insertResult.error?.message?.includes('permission denied') ||
          insertResult.error?.message?.includes('row-level security policy')) {
        
        console.log('🔄 Category create failed with RLS/Auth error, trying service role fallback...');
        
        // Try service role client for admin creates
        const { createClient } = await import('@supabase/supabase-js');
        
        const serviceRoleKey = import.meta.env.VITE_SUPABASE_SERVICE_ROLE_KEY || 
                              import.meta.env.SUPABASE_SERVICE_ROLE_KEY;
        
        if (serviceRoleKey && session?.user?.email === 'joey@towb.in') {
          console.log('🔑 Using service role for admin category create...');
          
          const serviceClient = createClient(
            import.meta.env.VITE_SUPABASE_URL!,
            serviceRoleKey,
            {
              auth: {
                persistSession: false
              }
            }
          );

          insertResult = await serviceClient
            .from('categories')
            .insert(categoryData);

          console.log('🔑 Service role category create result:', insertResult);
        }
      }

      if (insertResult.error) {
        console.error('❌ Final category create error:', insertResult.error);
        throw insertResult.error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-categories'] });
      setShowAddDialog(false);
      resetForm();
      toast.success('Category created successfully');
    },
    onError: (error: any) => {
      toast.error(`Failed to create category: ${error.message}`);
    }
  });

  // Update category mutation
  const updateCategoryMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<Category> }) => {
      // Check authentication first
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      console.log('🔐 Category update auth check:', {
        hasSession: !!session,
        userId: session?.user?.id,
        email: session?.user?.email,
        hasAccessToken: !!session?.access_token,
        tokenExpiry: session?.expires_at,
        tokenLength: session?.access_token?.length,
        supabaseUrl: import.meta.env.VITE_SUPABASE_URL,
        hasAnonKey: !!import.meta.env.VITE_SUPABASE_ANON_KEY
      });

      if (sessionError || !session) {
        console.error('❌ Category update - no valid session');
        throw new Error('You must be logged in to update categories');
      }

      // Log the exact data being sent
      console.log('📦 Update data payload:', {
        categoryId: id,
        updateData: data,
        dataKeys: Object.keys(data),
        dataValues: Object.values(data)
      });

      // Try normal update first
      console.log('🌐 Making PATCH request with supabase client...');
      
      // Check what headers the supabase client will send
      const supabaseConfig = (supabase as any).supabaseKey;
      console.log('🔑 Supabase client config check:', {
        hasSupabaseKey: !!supabaseConfig,
        authHeaderWillBeSet: !!session?.access_token,
        authHeaderValue: session?.access_token ? `Bearer ${session.access_token.substring(0, 20)}...` : 'none'
      });
      
      let updateResult = await supabase
        .from('categories')
        .update(data)
        .eq('id', id);

      console.log('📨 Initial update result:', {
        hasError: !!updateResult.error,
        errorMessage: updateResult.error?.message,
        errorCode: updateResult.error?.code,
        errorDetails: updateResult.error?.details,
        data: updateResult.data
      });

      // If update fails with 400/403 error, try service role fallback
      if (updateResult.error?.message?.includes('403') || 
          updateResult.error?.message?.includes('permission denied') ||
          updateResult.error?.message?.includes('row-level security policy')) {
        
        console.log('🔄 Category update failed with RLS/Auth error, trying service role fallback...');
        
        // Try service role client for admin updates
        const { createClient } = await import('@supabase/supabase-js');
        
        // Check if we have service role key available (admin users only)
        const serviceRoleKey = import.meta.env.VITE_SUPABASE_SERVICE_ROLE_KEY || 
                              import.meta.env.SUPABASE_SERVICE_ROLE_KEY;
        
        console.log('🔐 Service role availability check:', {
          hasServiceRoleKey: !!serviceRoleKey,
          serviceRoleKeyLength: serviceRoleKey?.length || 0,
          userEmail: session?.user?.email,
          isAdminUser: session?.user?.email === 'joey@towb.in'
        });
        
        if (serviceRoleKey && session?.user?.email === 'joey@towb.in') {
          console.log('🔑 Using service role for admin category update...');
          
          const serviceClient = createClient(
            import.meta.env.VITE_SUPABASE_URL!,
            serviceRoleKey,
            {
              auth: {
                persistSession: false
              }
            }
          );

          updateResult = await serviceClient
            .from('categories')
            .update(data)
            .eq('id', id);

          console.log('🔑 Service role category update result:', updateResult);
        } else {
          console.log('⚠️ Service role not available for category update');
        }
      }

      if (updateResult.error) {
        console.error('❌ Final category update error:', updateResult.error);
        throw updateResult.error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-categories'] });
      setEditingCategory(null);
      toast.success('Category updated successfully');
    },
    onError: (error: any) => {
      toast.error(`Failed to update category: ${error.message}`);
    }
  });

  // Delete category mutation
  const deleteCategoryMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('categories')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-categories'] });
      toast.success('Category deleted successfully');
    },
    onError: (error: any) => {
      toast.error(`Failed to delete category: ${error.message}`);
    }
  });

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      software_type: 'xLights',
      holiday_type: '',
      featured: false,
      image_url: '',
      image_alt: '',
      uploadedImage: null
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Use uploaded image URL if available, otherwise fall back to manual URL
    const imageUrl = formData.uploadedImage?.url || formData.image_url || null;
    
    if (editingCategory) {
      updateCategoryMutation.mutate({
        id: editingCategory.id,
        data: {
          name: formData.name,
          description: formData.description,
          software_type: formData.software_type,
          holiday_type: formData.holiday_type || null,
          featured: formData.featured,
          image_url: imageUrl,
          image_alt: formData.image_alt || null,
        }
      });
    } else {
      createCategoryMutation.mutate({
        ...formData,
        image_url: imageUrl
      });
    }
  };

  const startEditing = (category: Category) => {
    setEditingCategory(category);
    setFormData({
      name: category.name,
      description: category.description || '',
      software_type: category.software_type,
      holiday_type: category.holiday_type || '',
      featured: category.featured,
      image_url: category.image_url || '',
      image_alt: category.image_alt || '',
      uploadedImage: null
    });
  };

  const cancelEditing = () => {
    setEditingCategory(null);
    resetForm();
  };

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          Failed to load categories: {error.message}
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Categories Management</h2>
          <p className="text-muted-foreground">
            Manage sequence categories and their assignments
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex bg-muted rounded-lg p-1">
            <Button
              variant={activeView === 'categories' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setActiveView('categories')}
            >
              <Music className="h-4 w-4 mr-2" />
              Categories
            </Button>
            <Button
              variant={activeView === 'assignments' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setActiveView('assignments')}
            >
              <Settings className="h-4 w-4 mr-2" />
              Assignments
            </Button>
          </div>
          {activeView === 'categories' && (
            <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
              <DialogTrigger asChild>
                <Button onClick={() => resetForm()}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Category
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle>
                    {editingCategory ? 'Edit Category' : 'Add New Category'}
                  </DialogTitle>
                </DialogHeader>
                <CategoryForm 
                  formData={formData}
                  setFormData={setFormData}
                  onSubmit={handleSubmit}
                  onCancel={() => setShowAddDialog(false)}
                  isSubmitting={createCategoryMutation.isPending}
                />
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      {/* Content based on active view */}
      {activeView === 'categories' ? (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Music className="h-5 w-5" />
              Categories ({categories.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div>Loading categories...</div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Image</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Software</TableHead>
                    <TableHead>Holiday</TableHead>
                    <TableHead>Sequences</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {categories.map((category) => (
                    <TableRow key={category.id}>
                      <TableCell>
                        {category.image_url ? (
                          <img 
                            src={category.image_url} 
                            alt={category.image_alt || category.name}
                            className="w-12 h-12 object-cover rounded"
                          />
                        ) : (
                          <div className="w-12 h-12 bg-muted rounded flex items-center justify-center">
                            <ImageIcon className="h-4 w-4 text-muted-foreground" />
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        <div>
                          <div className="font-medium">{category.name}</div>
                          <div className="text-sm text-muted-foreground">
                            /{category.slug}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{category.software_type}</Badge>
                      </TableCell>
                      <TableCell>
                        {category.holiday_type ? (
                          <Badge variant="secondary">{category.holiday_type}</Badge>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {(category as any).sequence_count || 0}
                      </TableCell>
                      <TableCell>
                        {category.featured && (
                          <Badge variant="default">Featured</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              startEditing(category);
                              setShowAddDialog(true);
                            }}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              if (confirm('Are you sure you want to delete this category?')) {
                                deleteCategoryMutation.mutate(category.id);
                              }
                            }}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings className="h-5 w-5" />
              Category Sequence Assignments
            </CardTitle>
          </CardHeader>
          <CardContent>
            <CategorySequenceAssignment />
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function CategoryForm({ 
  formData, 
  setFormData, 
  onSubmit, 
  onCancel, 
  isSubmitting 
}: {
  formData: CategoryFormData;
  setFormData: (data: CategoryFormData) => void;
  onSubmit: (e: React.FormEvent) => void;
  onCancel: () => void;
  isSubmitting: boolean;
}) {
  return (
    <form onSubmit={onSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="name">Name</Label>
          <Input
            id="name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required
          />
        </div>
        <div>
          <Label htmlFor="software_type">Software Type</Label>
          <Select
            value={formData.software_type}
            onValueChange={(value) => setFormData({ ...formData, software_type: value })}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="xLights">xLights</SelectItem>
              <SelectItem value="Light-O-Rama">Light-O-Rama</SelectItem>
              <SelectItem value="Both">Both</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div>
        <Label htmlFor="description">Description</Label>
        <Textarea
          id="description"
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          rows={3}
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="holiday_type">Holiday Type (Optional)</Label>
          <Input
            id="holiday_type"
            value={formData.holiday_type}
            onChange={(e) => setFormData({ ...formData, holiday_type: e.target.value })}
            placeholder="e.g., Christmas, Halloween"
          />
        </div>
        <div className="flex items-center space-x-2 pt-6">
          <input
            type="checkbox"
            id="featured"
            checked={formData.featured}
            onChange={(e) => setFormData({ ...formData, featured: e.target.checked })}
          />
          <Label htmlFor="featured">Featured Category</Label>
        </div>
      </div>

      {/* Image Upload Section */}
      <div className="space-y-4">
        <Label>Category Image</Label>
        <CategoryImageUpload
          categoryName={formData.name}
          currentImageUrl={formData.image_url}
          onImageUploaded={(image) => {
            setFormData({ 
              ...formData, 
              uploadedImage: image,
              image_url: image.url 
            });
          }}
          onImageRemoved={() => {
            setFormData({ 
              ...formData, 
              uploadedImage: null,
              image_url: '' 
            });
          }}
          disabled={!formData.name}
          className="w-full"
        />
        
        {/* Alternative: Manual Image URL */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="image_url">Or enter Image URL manually</Label>
            <Input
              id="image_url"
              type="url"
              value={formData.image_url}
              onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
              placeholder="https://example.com/image.jpg"
              disabled={!!formData.uploadedImage}
            />
          </div>
          <div>
            <Label htmlFor="image_alt">Image Alt Text</Label>
            <Input
              id="image_alt"
              value={formData.image_alt}
              onChange={(e) => setFormData({ ...formData, image_alt: e.target.value })}
              placeholder="Describe the image"
            />
          </div>
        </div>
        
        {!formData.name && (
          <p className="text-sm text-muted-foreground">
            Enter a category name first to enable image upload
          </p>
        )}
      </div>

      <div className="flex justify-end gap-2 pt-4">
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
              Saving...
            </>
          ) : (
            <>
              <Check className="h-4 w-4 mr-2" />
              Save Category
            </>
          )}
        </Button>
      </div>
    </form>
  );
}